import { LoadingSpinner } from '@repo/atomic-components';
import { useEffect, useId, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { documentMarkdownComponents } from './markdownComponents';
import { usePdfTextSearch } from './usePdfTextSearch';
import { rtfToText } from '@/utils/rtfToText';
import { selectChatDocumentPanelSettings, useAppSelector } from '@/store';

export type TextFileViewerProps = {
  fileUrl: string;
  documentId: string;
  format: 'text' | 'markdown' | 'rtf';
  highlightText?: string;
  highlightTrigger?: number;
};

type LoadResult = { url: string; text: string | null; errored: boolean };

export function TextFileViewer({
  fileUrl,
  documentId,
  format,
  highlightText: highlightTextProp,
  highlightTrigger: highlightTriggerProp,
}: TextFileViewerProps) {
  const [result, setResult] = useState<LoadResult | null>(null);
  const loaded = result?.url === fileUrl ? result : null;
  const containerId = useId();

  // The side panel reads the citation from the store; the standalone viewer tab passes props.
  const allSettings = useAppSelector(selectChatDocumentPanelSettings);
  const panelSettings = allSettings[documentId];
  const highlightText = highlightTextProp ?? panelSettings?.highlightText;
  const highlightTrigger = highlightTriggerProp ?? panelSettings?.highlightTrigger;

  // Searched once the text is on screen, so a slow download doesn't use up the retries.
  usePdfTextSearch(loaded?.text ? highlightText : undefined, undefined, highlightTrigger, containerId);

  useEffect(() => {
    const abortController = new AbortController();

    fetch(fileUrl, { signal: abortController.signal })
      .then((response) => {
        if (!response.ok) throw new Error(`Failed to load file: ${response.status}`);
        return response.text();
      })
      .then((text) => setResult({ url: fileUrl, text: format === 'rtf' ? rtfToText(text) : text, errored: false }))
      .catch((error) => {
        if (abortController.signal.aborted) return;
        console.error('Failed to load text file preview:', error);
        setResult({ url: fileUrl, text: null, errored: true });
      });

    return () => abortController.abort();
  }, [fileUrl, format]);

  if (loaded?.errored) {
    return (
      <div className="text-theme-text-grey flex h-full w-full items-center justify-center p-6 text-center text-sm">
        Preview couldn’t be loaded.
      </div>
    );
  }

  if (!loaded || loaded.text === null) {
    return (
      <div className="flex h-full w-full items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="flex min-h-full w-full justify-center p-6">
      <div id={containerId} className="bg-theme-white h-fit w-full max-w-4xl rounded-sm p-8 shadow-md">
        {format === 'markdown' ? (
          <ReactMarkdown remarkPlugins={[remarkGfm]} components={documentMarkdownComponents}>
            {loaded.text}
          </ReactMarkdown>
        ) : (
          <pre className="text-theme-black font-sans text-sm leading-6 break-words whitespace-pre-wrap">
            {loaded.text}
          </pre>
        )}
      </div>
    </div>
  );
}
